import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { cardClasses } from '../../styles/tailwindClasses';
import { analyticsPanelSx } from '../../styles/analyticsMsiStyles';
import { posSizingTableTitleSx } from '../pos_sizing_table/tableStyles';
import {
  WORKSPACE_DIMENSIONS,
  optimizerWorkspacePanelSx,
  workspacePanelDescriptionSx,
  workspacePanelHeadingSx,
} from '../../styles/workspaceChromeStyles';
import { constraintTabsSx } from './OptimizerRuleTableParts';
import ActiveLimitsTable from './ActiveLimitsTable';
import AttributeConstraintCard from './AttributeConstraintCard';
import ByFactorOptimizerRiskTable from './ByFactorOptimizerRiskTable';
import PortfolioRulesTable from './PortfolioRulesTable';
import { constructionLimitCount } from './ManualConstructionRows';
import {
  baselineColumnLabel,
  indexConstraintEvaluation,
  listActiveRules,
} from './optimizerRuleRegistry';

const SCOPES = Object.freeze([
  { value: 'portfolio', label: 'Portfolio' },
  { value: 'factor', label: 'Factors' },
  { value: 'attribute', label: 'Attributes' },
  { value: 'active', label: 'Active Limits' },
]);

// One workspace for every constraint scope. Each tab is a different view of
// the same editor records, and hidden tabs stay mounted so switching scopes
// keeps local choices such as the selected attribute and sort order.
export default function OptimizerConstraintsPanel({
  constraints,
  constraintToggles,
  gmvBudget = null,
  groupConstraints,
  factorNames,
  risksByFactor,
  attributeDefinitions,
  optimizerAttributeRiskMetrics,
  constraintEvaluation,
  resultStale = false,
  viewMode,
  actions,
  groupRecordLimit,
  attributeMetadataReady,
  disabled,
  optimizerIsRunning,
  viewModeLoading,
  onResetLimits,
  cardinalityAvailable = false,
  securityControlCounts = null,
  objective = 'AlphaScore',
  onViewChangedNames = null,
  height = WORKSPACE_DIMENSIONS.optimizerStackHeight,
  searchRanges = null,
  rangeEditor = null,
  modeEditor = null,
  searchContent = null,
  searchSettings = null,
  onScopeChange,
  scope = null,
}) {
  const [localScope, setActiveScope] = useState(SCOPES[0].value);
  const activeScope = scope ?? localScope;
  const evaluationIndex = useMemo(
    () => indexConstraintEvaluation(constraintEvaluation),
    [constraintEvaluation],
  );
  const baselineLabel = baselineColumnLabel(constraintEvaluation, viewMode);
  const activeRules = useMemo(() => listActiveRules({
    constraints,
    constraintToggles,
    groupConstraints,
    attributeDefinitions,
  }), [attributeDefinitions, constraintToggles, constraints, groupConstraints]);

  const panel = (scope, content) => (
    <Box
      role="tabpanel"
      id={`optimizer-constraints-tabpanel-${scope}`}
      aria-labelledby={`optimizer-constraints-tab-${scope}`}
      hidden={activeScope !== scope}
      sx={{
        flex: activeScope === scope ? 1 : 0,
        minHeight: 0,
        display: activeScope === scope ? 'flex' : 'none',
        flexDirection: 'column',
        ...(scope === 'search' ? { flex: 'none' } : {}),
      }}
    >
      {content}
    </Box>
  );

  return (
    <Paper
      className={cardClasses}
      aria-label={modeEditor ? 'Constraints & Search' : 'Constraints'}
      elevation={0}
      sx={[{
        ...optimizerWorkspacePanelSx,
        minWidth: 0,
        // Search grows with its chart and stacked outcome on smaller screens.
        // Keep one page scroll instead of cropping the plot inside a short card.
        height: activeScope === 'search' ? 'auto' : height,
        minHeight: height,
        display: 'flex',
        flexDirection: 'column',
        border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.9)}`,
      }, analyticsPanelSx]}
    >
      <Box sx={{ display: 'flex', flexShrink: 0, alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
        <Box sx={{ ...workspacePanelHeadingSx, flex: 1 }}>
          <Typography variant="subtitle1" sx={posSizingTableTitleSx}>
            {modeEditor ? 'Constraints & Search' : 'Constraints'}
          </Typography>
          <Typography sx={workspacePanelDescriptionSx}>
            {modeEditor ? 'Fixed keeps your limit. Auto searches for portfolios while keeping every Fixed limit.' : 'Set each limit beside the value it governs. Values come from the displayed run.'}
          </Typography>
        </Box>
        <Button
          variant="text"
          size="small"
          onClick={onResetLimits}
          disabled={disabled}
          sx={{ minWidth: 0, whiteSpace: 'nowrap', textTransform: 'none' }}
        >
          Reset limits
        </Button>
      </Box>

      <Tabs
        value={activeScope}
        onChange={(_event, value) => { setActiveScope(value); onScopeChange?.(value); }}
        aria-label="Constraint scope"
        sx={constraintTabsSx}
      >
        {(modeEditor ? [...SCOPES, { value: 'search', label: 'Search' }] : SCOPES).map((scope) => (
          <Tab
            key={scope.value}
            value={scope.value}
            label={scope.value === 'active'
              ? `${scope.label} (${activeRules.length + constructionLimitCount(constraints.design)})`
              : scope.label}
            id={`optimizer-constraints-tab-${scope.value}`}
            aria-controls={`optimizer-constraints-tabpanel-${scope.value}`}
          />
        ))}
      </Tabs>

      {activeScope === 'search' && searchSettings && (
        <Box role="group" aria-label="Optimizer run controls"
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', my: 1.5, flexShrink: 0 }}>
          {searchSettings}
        </Box>
      )}

      {panel('portfolio', (
        <PortfolioRulesTable
          constraints={constraints}
          searchRanges={searchRanges}
          rangeEditor={rangeEditor}
          modeEditor={modeEditor}
          constraintToggles={constraintToggles}
          gmvBudget={gmvBudget}
          evaluationIndex={evaluationIndex}
          baselineLabel={baselineLabel}
          actions={actions}
          resultStale={resultStale}
          disabled={disabled}
          cardinalityAvailable={cardinalityAvailable}
          securityControlCounts={securityControlCounts}
          objective={objective}
          onViewChangedNames={onViewChangedNames}
        />
      ))}
      {panel('factor', (
        <ByFactorOptimizerRiskTable
          factorNames={factorNames}
          risksByFactor={risksByFactor}
          constraints={constraints}
          searchRanges={searchRanges}
          rangeEditor={rangeEditor}
          modeEditor={modeEditor}
          evaluationIndex={evaluationIndex}
          baselineLabel={baselineLabel}
          actions={actions}
          resultStale={resultStale}
          disabled={disabled}
          optimizerIsRunning={optimizerIsRunning}
          viewModeLoading={viewModeLoading}
        />
      ))}
      {panel('attribute', (
        <AttributeConstraintCard
          attributeDefinitions={attributeDefinitions}
          searchRanges={searchRanges}
          rangeEditor={rangeEditor}
          modeEditor={modeEditor}
          attributeRiskMetrics={optimizerAttributeRiskMetrics}
          groupConstraints={groupConstraints}
          evaluationIndex={evaluationIndex}
          baselineLabel={baselineLabel}
          actions={actions}
          recordLimit={groupRecordLimit}
          resultStale={resultStale}
          metadataReady={attributeMetadataReady}
          disabled={disabled}
          riskLoading={optimizerIsRunning || viewModeLoading}
        />
      ))}
      {searchContent && panel('search', searchContent)}
      {panel('active', (
        <ActiveLimitsTable
          construction={{ constraints, objective }}
          searchRanges={searchRanges}
          rangeEditor={rangeEditor}
          modeEditor={modeEditor}
          activeRules={activeRules}
          evaluationIndex={evaluationIndex}
          baselineLabel={baselineLabel}
          actions={actions}
          resultStale={resultStale}
          disabled={disabled}
        />
      ))}
    </Paper>
  );
}

OptimizerConstraintsPanel.propTypes = {
  constraints: PropTypes.object.isRequired,
  constraintToggles: PropTypes.object.isRequired,
  gmvBudget: PropTypes.shape({
    auto: PropTypes.bool,
    checkedGmv: PropTypes.number,
    followChecked: PropTypes.func,
  }),
  groupConstraints: PropTypes.array.isRequired,
  factorNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  risksByFactor: PropTypes.array,
  attributeDefinitions: PropTypes.array,
  optimizerAttributeRiskMetrics: PropTypes.object,
  constraintEvaluation: PropTypes.object,
  resultStale: PropTypes.bool,
  viewMode: PropTypes.string,
  actions: PropTypes.object.isRequired,
  groupRecordLimit: PropTypes.number.isRequired,
  attributeMetadataReady: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  optimizerIsRunning: PropTypes.bool,
  viewModeLoading: PropTypes.bool,
  onResetLimits: PropTypes.func,
  cardinalityAvailable: PropTypes.bool,
  securityControlCounts: PropTypes.objectOf(PropTypes.number),
  objective: PropTypes.string,
  onViewChangedNames: PropTypes.func,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  searchSettings: PropTypes.node,
};
